import { CSSProperties } from "react";
import { TCropCoords } from "../../../../../utils/types";
import {
  clampToRange,
  padAndBoundRect,
  parseFiniteRectTuple,
} from "../../../../../utils/censorshipGeometry";
import { TSideBySideCensorship } from "../../../../../services/api-clinic";

export { DEFAULT_CENSORSHIP_STICKER_URL as CENSORSHIP_STICKER_URL } from "../../../../../config/stickerPresets";

type TTemplateImageFrame = {
  position: { x: number; y: number };
  size: { width: string | number; height: string | number };
  imageStyle?: CSSProperties;
};

type TTemplateOuterContainer = {
  width?: string | number;
  height?: string | number;
};

export type TCensorshipStickerPlacement = {
  key: string;
  regionKey: string;
  censorshipSide: "before" | "after";
  left: number;
  top: number;
  width: number;
  height: number;
};

const DEFAULT_TEMPLATE_DIMENSION = 1000;
const STICKER_PADDING_RATIO = 0.2;
const MIN_STICKER_SIDE = 16;

const parseDimension = (
  value: string | number | undefined,
  base: number,
): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(1, value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.endsWith("%")) {
      const percent = Number.parseFloat(trimmed);
      if (Number.isFinite(percent)) {
        return Math.max(1, (base * percent) / 100);
      }
    }

    const numeric = Number.parseFloat(trimmed);
    if (Number.isFinite(numeric)) {
      return Math.max(1, numeric);
    }
  }

  return Math.max(1, base);
};

const getObjectFitScale = (
  fit: string,
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
) => {
  if (fit === "contain" || fit === "cover") {
    const scale =
      fit === "cover"
        ? Math.max(containerWidth / imageWidth, containerHeight / imageHeight)
        : Math.min(containerWidth / imageWidth, containerHeight / imageHeight);

    const renderWidth = imageWidth * scale;
    const renderHeight = imageHeight * scale;

    return {
      scaleX: scale,
      scaleY: scale,
      offsetX: (containerWidth - renderWidth) / 2,
      offsetY: (containerHeight - renderHeight) / 2,
    };
  }

  return {
    scaleX: containerWidth / imageWidth,
    scaleY: containerHeight / imageHeight,
    offsetX: 0,
    offsetY: 0,
  };
};

export const getOuterContainerDimensions = (
  outerContainer: TTemplateOuterContainer,
) => ({
  width: parseDimension(outerContainer.width, DEFAULT_TEMPLATE_DIMENSION),
  height: parseDimension(outerContainer.height, DEFAULT_TEMPLATE_DIMENSION),
});

export const getCensorshipStickerPlacements = ({
  imageFrame,
  cropCoords,
  censorship,
  outerWidth,
  outerHeight,
  keyPrefix,
  rotationDegrees = 0,
}: {
  imageFrame: TTemplateImageFrame;
  cropCoords?: TCropCoords;
  censorship?: TSideBySideCensorship | null;
  outerWidth: number;
  outerHeight: number;
  keyPrefix: "before" | "after";
  rotationDegrees?: number;
}): TCensorshipStickerPlacement[] => {
  if (!cropCoords || !censorship) {
    return [];
  }

  const bboxEntries = Object.entries(censorship.finalBboxesOnOriginal || {});
  if (!bboxEntries.length) {
    return [];
  }

  const containerWidth = parseDimension(imageFrame.size.width, outerWidth);
  const containerHeight = parseDimension(imageFrame.size.height, outerHeight);
  const sourceImageWidth = Math.max(1, cropCoords.cropWidth);
  const sourceImageHeight = Math.max(1, cropCoords.cropHeight);
  const normalizedRotation = ((rotationDegrees % 360) + 360) % 360;
  const isSideways = normalizedRotation === 90 || normalizedRotation === 270;
  const imageWidth = isSideways ? sourceImageHeight : sourceImageWidth;
  const imageHeight = isSideways ? sourceImageWidth : sourceImageHeight;
  const objectFit =
    typeof imageFrame.imageStyle?.objectFit === "string"
      ? imageFrame.imageStyle.objectFit
      : "fill";
  const { scaleX, scaleY, offsetX, offsetY } = getObjectFitScale(
    objectFit,
    containerWidth,
    containerHeight,
    imageWidth,
    imageHeight,
  );
  const slotMinX = imageFrame.position.x;
  const slotMinY = imageFrame.position.y;
  const slotMaxX = slotMinX + containerWidth;
  const slotMaxY = slotMinY + containerHeight;
  const rotatePoint = (x: number, y: number) => {
    switch (normalizedRotation) {
      case 90:
        return { x: sourceImageHeight - y, y: x };
      case 180:
        return { x: sourceImageWidth - x, y: sourceImageHeight - y };
      case 270:
        return { x: y, y: sourceImageWidth - x };
      default:
        return { x, y };
    }
  };

  return bboxEntries.flatMap(([regionKey, bbox], index) => {
    const parsedBbox = parseFiniteRectTuple(bbox);
    if (!parsedBbox) return [];

    const rawX1 = parsedBbox.x1 - cropCoords.cropX;
    const rawY1 = parsedBbox.y1 - cropCoords.cropY;
    const rawX2 = parsedBbox.x2 - cropCoords.cropX;
    const rawY2 = parsedBbox.y2 - cropCoords.cropY;
    const croppedX1 = clampToRange(rawX1, 0, sourceImageWidth);
    const croppedY1 = clampToRange(rawY1, 0, sourceImageHeight);
    const croppedX2 = clampToRange(rawX2, 0, sourceImageWidth);
    const croppedY2 = clampToRange(rawY2, 0, sourceImageHeight);

    if (croppedX2 <= croppedX1 || croppedY2 <= croppedY1) {
      return [];
    }

    const rotatedCorners = [
      rotatePoint(croppedX1, croppedY1),
      rotatePoint(croppedX2, croppedY1),
      rotatePoint(croppedX2, croppedY2),
      rotatePoint(croppedX1, croppedY2),
    ];
    const rotatedXValues = rotatedCorners.map((point) => point.x);
    const rotatedYValues = rotatedCorners.map((point) => point.y);
    const rotatedX1 = Math.min(...rotatedXValues);
    const rotatedY1 = Math.min(...rotatedYValues);
    const rotatedX2 = Math.max(...rotatedXValues);
    const rotatedY2 = Math.max(...rotatedYValues);

    const placement = padAndBoundRect({
      rect: {
        left: slotMinX + offsetX + rotatedX1 * scaleX,
        top: slotMinY + offsetY + rotatedY1 * scaleY,
        width: (rotatedX2 - rotatedX1) * scaleX,
        height: (rotatedY2 - rotatedY1) * scaleY,
      },
      bounds: {
        minX: slotMinX,
        minY: slotMinY,
        maxX: slotMaxX,
        maxY: slotMaxY,
      },
      paddingRatio: STICKER_PADDING_RATIO,
      minimumPadding: 2,
      minimumWidth: MIN_STICKER_SIDE,
      minimumHeight: MIN_STICKER_SIDE,
      mode: "clip-padded-edges",
    });
    if (!placement) return [];

    return [
      {
        key: `${keyPrefix}-${regionKey}-${index}`,
        regionKey,
        censorshipSide: keyPrefix,
        left: placement.left,
        top: placement.top,
        width: placement.width,
        height: placement.height,
      },
    ];
  });
};
